import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Bell, Loader2, Send, RadioTower } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAlertConfig() {
    const [chatId, setChatId] = useState('');
    const [configured, setConfigured] = useState(false);
    const [busy, setBusy] = useState('');
    const [detected, setDetected] = useState(null);

    useEffect(() => {
        axios.get(`${API}/admin/telegram/admin-config`, { withCredentials: true })
            .then((r) => { setChatId(r.data.chat_id || ''); setConfigured(r.data.configured); })
            .catch(() => {});
    }, []);

    const save = async () => {
        setBusy('save');
        try {
            const r = await axios.put(`${API}/admin/telegram/admin-config`, { chat_id: chatId }, { withCredentials: true });
            setConfigured(r.data.configured);
            toast.success('Approval alerts chat saved');
        } catch { toast.error('Failed to save'); }
        setBusy('');
    };

    const detect = async () => {
        setBusy('detect');
        try {
            const r = await axios.post(`${API}/admin/telegram/admin-detect`, {}, { withCredentials: true });
            if (r.data.ok && r.data.chats?.length) setDetected(r.data.chats);
            else toast.info(r.data.error || 'No private chats found — open Telegram and send /start to your bot first, then retry.');
        } catch { toast.error('Detect failed'); }
        setBusy('');
    };

    const test = async () => {
        setBusy('test');
        try {
            const r = await axios.post(`${API}/admin/telegram/admin-test`, {}, { withCredentials: true });
            if (r.data.ok) toast.success('Test message sent — check your Telegram!');
            else toast.error(r.data.error || 'Test failed');
        } catch { toast.error('Test failed'); }
        setBusy('');
    };

    return (
        <div className="card-surface p-5 mb-8" data-testid="admin-alert-config">
            <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(255,184,0,0.12)', border: '1px solid rgba(184,134,11,0.35)' }}>
                    <Bell className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                </div>
                <div>
                    <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                        Telegram Approval Alerts
                        {configured && <span className="ml-2 text-xs font-semibold text-emerald-600">Connected ✓</span>}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                        Get a Telegram DM the moment a student uploads a deposit screenshot. Send <code className="font-mono">/start</code> to your bot, click Detect, then Save.
                    </p>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="text"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="Your Telegram chat ID (e.g. 123456789)"
                    className="input-field flex-1 font-mono text-sm"
                    data-testid="admin-alert-chatid-input"
                />
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={detect} disabled={!!busy} data-testid="admin-alert-detect-btn">
                        {busy === 'detect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RadioTower className="w-4 h-4 mr-1.5" />} Detect
                    </Button>
                    <Button size="sm" onClick={save} disabled={!!busy || !chatId.trim()} className="btn-primary" data-testid="admin-alert-save-btn">
                        {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={test} disabled={!!busy || !configured} data-testid="admin-alert-test-btn">
                        {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Test</>}
                    </Button>
                </div>
            </div>
            {detected && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-alert-detected-list">
                    {detected.map((c) => (
                        <button
                            key={c.chat_id}
                            onClick={() => { setChatId(c.chat_id); setDetected(null); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors hover:border-[var(--border-highlight)]"
                            style={{ backgroundColor: 'var(--background-elevated)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                        >
                            {c.title} · <span className="font-mono">{c.chat_id}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
